/**
 * Power BI API utility.
 *
 * Supports two modes:
 * - **Live mode**: Uses MSAL tokens to call the authenticated backend API
 * - **Demo mode**: Calls the unauthenticated /api/demo endpoints with sample data
 *
 * Demo mode is enabled when REACT_APP_DEMO_MODE=true or when no client ID is configured.
 */

import { powerbiRequest } from "../authConfig";

// Re-export all types from the centralized types file
export type {
  PortcoListItem,
  PortcoMetrics,
  PeriodMetrics,
  ChannelData,
  DailyTrendPoint,
  WeeklyTrendPoint,
  MonthlyTrendPoint,
  InventoryTrendPoint,
  CountryTrendPoint,
  CountrySummaryRow,
  CountrySummary,
  OrderLineItem,
  OrderDetail,
  OrderDetailResponse,
  MonthAccuracyRecord,
  ForecastSnapshotPoint,
  ForecastAccuracyData,
  ForecastStoreData,
  PowerBIRefreshEntry,
  DatasetRefreshInfo,
  PipelineRunEntry,
  PipelineInfo,
  UserInfo,
  EmailPreviewData,
  NetSuiteRow,
  NetSuiteDataResponse,
  SalesPeriod,
  SalesByItemRow,
  InventoryInsight,
  InventoryInsightsResponse,
  EcommerceBrand,
  DailyAnalytics,
  MonthlyRevenue,
  RepeatRateData,
  EcommerceOverview,
  ProductABC,
  CohortRow,
  RefundRow,
  TrafficRow,
  AmazonOverview,
  AmazonMonthlyProfitability,
  AmazonTopSku,
  AmazonInventoryHealth,
  AmazonReturnsSku,
  EmailSchedule,
  CreateEmailScheduleInput,
  UpdateEmailScheduleInput,
  SchedulePortcoOption,
  EmailLogEntry,
  SchedulerRunEntry,
  EmailHtmlResponse,
  TemplateMetadata,
  PromotionRecord,
} from "../types/api";

import type {
  PortcoListItem,
  PortcoMetrics,
  WeeklyTrendPoint,
  MonthlyTrendPoint,
  InventoryTrendPoint,
  OrderDetailResponse,
  ForecastAccuracyData,
  ForecastStoreData,
  DatasetRefreshInfo,
  PipelineInfo,
  UserInfo,
  FundSummary,
  EmailPreviewData,
  NetSuiteDataResponse,
  NetSuiteRow,
  SalesByItemRow,
  SalesPeriod,
  InventoryInsightsResponse,
  EcommerceBrand,
  EcommerceOverview,
  MonthlyRevenue,
  ProductABC,
  CohortRow,
  RefundRow,
  TrafficRow,
  AmazonOverview,
  AmazonMonthlyProfitability,
  AmazonTopSku,
  AmazonInventoryHealth,
  AmazonReturnsSku,
  EmailSchedule,
  CreateEmailScheduleInput,
  UpdateEmailScheduleInput,
  SchedulePortcoOption,
  EmailLogEntry,
  SchedulerRunEntry,
  EmailHtmlResponse,
  TemplateMetadata,
} from "../types/api";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

// Backstop above the server's own 60s Power BI timeout, so a request that
// gets stuck upstream surfaces as an error instead of an endless spinner.
const REQUEST_TIMEOUT = 90000; // 90s

/**
 * Lazily get the MSAL instance to avoid circular import issues.
 * Only called in live mode — never in demo mode.
 */
function getMsalInstance() {
  // eslint-disable-next-line
  const { msalInstance } = require("../index");
  return msalInstance;
}

/**
 * Check if the app is running in demo mode.
 */
export function isDemoMode(): boolean {
  return (
    process.env.REACT_APP_DEMO_MODE === "true" ||
    !process.env.REACT_APP_CLIENT_ID
  );
}

/**
 * Get an ID token for authenticating with the backend API.
 */
async function getIdToken(forceRefresh = false): Promise<string> {
  const msal = getMsalInstance();
  const account = msal.getActiveAccount();
  if (!account) {
    throw new Error(
      "No active account! Verify a user has been signed in and setActiveAccount has been called.",
    );
  }

  const tokenResponse = await msal.acquireTokenSilent({
    ...powerbiRequest,
    account,
    forceRefresh,
    redirectUri: "/blank.html",
  });

  if (!tokenResponse.idToken) {
    throw new Error("No ID token received from MSAL");
  }

  return tokenResponse.idToken;
}

/**
 * One authenticated fetch to the backend API, bounded by REQUEST_TIMEOUT.
 */
async function apiFetch(
  endpoint: string,
  options: RequestInit,
  token: string,
): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    if (err instanceof Error && err.name === "TimeoutError") {
      throw new Error(
        `Request timed out after ${REQUEST_TIMEOUT / 1000}s. The data source may be unavailable — try again shortly.`,
      );
    }
    throw err;
  }
}

/**
 * Make an authenticated request to the backend API.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getIdToken();

  const response = await apiFetch(endpoint, options, token);

  // If 401 (e.g. expired ID token from cache), force-refresh and retry once
  if (response.status === 401) {
    const freshToken = await getIdToken(true);
    const retryResponse = await apiFetch(endpoint, options, freshToken);
    if (!retryResponse.ok) {
      const errorBody = await retryResponse.json().catch(() => ({
        error: `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
      }));
      throw new Error(
        errorBody.error || `API request failed: ${retryResponse.status}`,
      );
    }
    const data = await retryResponse.json();
    return data.data as T;
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(
      errorBody.error || `API request failed: ${response.status}`,
    );
  }

  const data = await response.json();
  return data.data as T;
}

/**
 * Make an unauthenticated request to the demo API.
 */
export async function demoRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_BASE}/demo${endpoint}`, {
    ...options,
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(
      errorBody.error || `Demo API request failed: ${response.status}`,
    );
  }

  const data = await response.json();
  return data.data as T;
}

// ============================================================================
// Portco Data
// ============================================================================

export async function getPortcos(): Promise<PortcoListItem[]> {
  if (isDemoMode()) return demoRequest<PortcoListItem[]>("/portcos");
  return apiRequest<PortcoListItem[]>("/portcos");
}

export async function getPortcoMetrics(
  portcoId: string,
  reportDate?: string,
): Promise<PortcoMetrics> {
  const qs = reportDate ? `?reportDate=${encodeURIComponent(reportDate)}` : "";
  if (isDemoMode())
    return demoRequest<PortcoMetrics>(`/portcos/${portcoId}/metrics${qs}`);
  return apiRequest<PortcoMetrics>(`/portcos/${portcoId}/metrics${qs}`);
}

export async function getPortcoWeeklyTrend(
  portcoId: string,
): Promise<WeeklyTrendPoint[] | null> {
  try {
    if (isDemoMode())
      return await demoRequest<WeeklyTrendPoint[]>(
        `/portcos/${portcoId}/weekly-trend`,
      );
    return await apiRequest<WeeklyTrendPoint[]>(
      `/portcos/${portcoId}/weekly-trend`,
    );
  } catch {
    return null;
  }
}

export async function getPortcoMonthlyTrend(
  portcoId: string,
): Promise<MonthlyTrendPoint[] | null> {
  try {
    if (isDemoMode())
      return await demoRequest<MonthlyTrendPoint[]>(
        `/portcos/${portcoId}/monthly-trend`,
      );
    return await apiRequest<MonthlyTrendPoint[]>(
      `/portcos/${portcoId}/monthly-trend`,
    );
  } catch {
    return null;
  }
}

export async function getPortcoInventoryTrend(
  portcoId: string,
): Promise<InventoryTrendPoint[] | null> {
  try {
    if (isDemoMode())
      return await demoRequest<InventoryTrendPoint[]>(
        `/portcos/${portcoId}/inventory-trend`,
      );
    return await apiRequest<InventoryTrendPoint[]>(
      `/portcos/${portcoId}/inventory-trend`,
    );
  } catch {
    return null;
  }
}

export async function getOrderDetail(
  portcoId: string,
): Promise<OrderDetailResponse | null> {
  try {
    if (isDemoMode())
      return await demoRequest<OrderDetailResponse>(
        `/portcos/${portcoId}/order-detail`,
      );
    return await apiRequest<OrderDetailResponse>(
      `/portcos/${portcoId}/order-detail`,
    );
  } catch {
    return null;
  }
}

// ============================================================================
// Forecast Accuracy
// ============================================================================

export async function getPortcoForecastAccuracy(
  portcoId: string,
): Promise<ForecastAccuracyData | null> {
  try {
    if (isDemoMode())
      return await demoRequest<ForecastAccuracyData>(
        `/portcos/${portcoId}/forecast-accuracy`,
      );
    return await apiRequest<ForecastAccuracyData>(
      `/portcos/${portcoId}/forecast-accuracy`,
    );
  } catch {
    return null;
  }
}

export async function getForecastStore(): Promise<ForecastStoreData> {
  if (isDemoMode()) return demoRequest<ForecastStoreData>("/forecast-store");
  return apiRequest<ForecastStoreData>("/forecast-store");
}

export async function triggerSnapshot(): Promise<{
  message: string;
  timestamp: string;
  store: ForecastStoreData;
}> {
  if (isDemoMode()) {
    const store = await demoRequest<ForecastStoreData>("/forecast-store");
    return {
      message: "Demo snapshot (no-op)",
      timestamp: new Date().toISOString(),
      store,
    };
  }
  return apiRequest("/forecast-store/snapshot", { method: "POST" });
}

// ============================================================================
// Dataset Refresh & Pipelines
// ============================================================================

export async function getDatasetRefreshes(): Promise<DatasetRefreshInfo[]> {
  if (isDemoMode())
    return demoRequest<DatasetRefreshInfo[]>("/dataset-refreshes");
  return apiRequest<DatasetRefreshInfo[]>("/dataset-refreshes");
}

export async function triggerDatasetRefresh(
  datasetId: string,
  workspaceId: string,
): Promise<{ message: string; datasetId: string; timestamp: string }> {
  if (isDemoMode()) {
    return demoRequest(`/dataset-refreshes/${datasetId}/refresh`);
  }
  return apiRequest(
    `/dataset-refreshes/${datasetId}/refresh?workspaceId=${encodeURIComponent(workspaceId)}`,
    { method: "POST" },
  );
}

export async function getPipelineRuns(
  force?: boolean,
): Promise<PipelineInfo[]> {
  const qs = force ? "?force=true" : "";
  if (isDemoMode()) return demoRequest<PipelineInfo[]>(`/pipeline-runs${qs}`);
  return apiRequest<PipelineInfo[]>(`/pipeline-runs${qs}`);
}

export async function triggerPipelineRun(
  system: string,
  pipelineName: string,
): Promise<{ runId: string; message: string }> {
  if (isDemoMode()) {
    return demoRequest(
      `/pipeline-runs/${system}/${encodeURIComponent(pipelineName)}/trigger`,
    );
  }
  return apiRequest(
    `/pipeline-runs/${system}/${encodeURIComponent(pipelineName)}/trigger`,
    { method: "POST" },
  );
}

// ============================================================================
// Admin & Debug
// ============================================================================

export async function getPortcoDebug(portcoId: string): Promise<unknown> {
  return apiRequest<unknown>(`/portcos/${portcoId}/debug`);
}

export async function sendPortcoEmail(
  portcoId: string,
  reportDate?: string,
  recipients?: string[],
): Promise<{
  message: string;
  recipient: string;
  recipients?: string[];
}> {
  const payload: Record<string, unknown> = {};
  if (reportDate) payload.reportDate = reportDate;
  if (recipients && recipients.length > 0) payload.recipients = recipients;
  const hasBody = Object.keys(payload).length > 0;
  return apiRequest<{
    message: string;
    recipient: string;
    recipients?: string[];
  }>(`/reports/${portcoId}/send-email`, {
    method: "POST",
    body: hasBody ? JSON.stringify(payload) : undefined,
  });
}

export async function getUserInfo(): Promise<UserInfo> {
  return apiRequest<UserInfo>("/me");
}

/** Fund performance Summary at a quarter-end (omit asOf for the latest). WMP tenant only. */
export async function getFundSummary(asOf?: string): Promise<FundSummary> {
  const path =
    "/fund/summary" + (asOf ? "?asOf=" + encodeURIComponent(asOf) : "");
  return isDemoMode()
    ? demoRequest<FundSummary>(path)
    : apiRequest<FundSummary>(path);
}

export async function getEmailPreview(
  portcoId: string,
  calloutMessage?: string,
  reportDate?: string,
): Promise<EmailPreviewData> {
  const params = new URLSearchParams();
  if (calloutMessage) params.set("calloutMessage", calloutMessage);
  if (reportDate) params.set("reportDate", reportDate);
  const qs = params.toString() ? `?${params.toString()}` : "";
  if (isDemoMode())
    return demoRequest<EmailPreviewData>(
      `/portcos/${portcoId}/preview-email${qs}`,
    );
  return apiRequest<EmailPreviewData>(
    `/reports/${portcoId}/preview-email${qs}`,
  );
}

export async function getRateLimitStatus(): Promise<{
  requestsUsed: number;
  requestsRemaining: number;
  windowMs: number;
}> {
  return apiRequest("/rate-limit");
}

export async function clearApiCache(): Promise<void> {
  if (isDemoMode()) return;
  await apiRequest<void>("/cache/clear", { method: "POST" });
}

// ============================================================================
// NetSuite
// ============================================================================

export async function getPortcoNetSuiteData(
  portcoId: string,
): Promise<NetSuiteDataResponse> {
  if (isDemoMode()) return demoRequest(`/portcos/${portcoId}/netsuite`);
  return apiRequest(`/portcos/${portcoId}/netsuite`);
}

export async function getPortcoNetSuiteQuery(
  portcoId: string,
  queryName: string,
  params?: Record<string, string>,
): Promise<NetSuiteRow[]> {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  if (isDemoMode())
    return demoRequest(`/portcos/${portcoId}/netsuite/${queryName}${qs}`);
  return apiRequest(`/portcos/${portcoId}/netsuite/${queryName}${qs}`);
}

export async function getSalesByItem(
  portcoId: string,
  period: SalesPeriod,
): Promise<SalesByItemRow[]> {
  const rows = await getPortcoNetSuiteQuery(portcoId, "salesByItem", {
    period,
  });
  return rows.map((row) => ({
    item_name: String(row.item_name ?? ""),
    item_type: String(row.item_type ?? ""),
    display_name: String(row.display_name ?? ""),
    product_category: String(row.product_category ?? ""),
    product_category_2: String(row.product_category_2 ?? ""),
    total_sales: Number(row.total_sales ?? 0),
    total_cost: Number(row.total_cost ?? 0),
    total_quantity: Number(row.total_quantity ?? 0),
  }));
}

// ============================================================================
// AI Inventory Insights
// ============================================================================

export async function getInventoryInsights(
  portcoId: string,
): Promise<InventoryInsightsResponse> {
  if (isDemoMode()) {
    const response = await fetch(
      `${API_BASE}/demo/ai/inventory-insights/${portcoId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!response.ok)
      throw new Error(`Insights request failed (${response.status})`);
    const data = await response.json();
    return data.data as InventoryInsightsResponse;
  }
  return await apiRequest<InventoryInsightsResponse>(
    `/ai/inventory-insights/${portcoId}`,
    { method: "POST" },
  );
}

export async function getCachedInventoryInsights(
  portcoId: string,
): Promise<InventoryInsightsResponse | null> {
  if (isDemoMode()) return null;
  const token = await getIdToken();
  const response = await fetch(
    `${API_BASE}/ai/inventory-insights/${portcoId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  if (response.status === 204) return null;
  if (!response.ok) return null;
  const data = await response.json();
  return data.data as InventoryInsightsResponse;
}

// ============================================================================
// E-commerce
// ============================================================================

export async function getEcommerceBrands(): Promise<EcommerceBrand[]> {
  if (isDemoMode()) return demoRequest<EcommerceBrand[]>("/ecommerce/brands");
  return apiRequest<EcommerceBrand[]>("/ecommerce/brands");
}

export async function getEcommerceOverview(
  brandCode: string,
): Promise<EcommerceOverview> {
  if (isDemoMode())
    return demoRequest<EcommerceOverview>(`/ecommerce/${brandCode}/overview`);
  return apiRequest<EcommerceOverview>(`/ecommerce/${brandCode}/overview`);
}

export async function getEcommerceRevenueTrend(
  brandCode: string,
): Promise<MonthlyRevenue[]> {
  if (isDemoMode())
    return demoRequest<MonthlyRevenue[]>(
      `/ecommerce/${brandCode}/revenue-trend`,
    );
  return apiRequest<MonthlyRevenue[]>(`/ecommerce/${brandCode}/revenue-trend`);
}

export async function getEcommerceProducts(
  brandCode: string,
): Promise<ProductABC[]> {
  if (isDemoMode())
    return demoRequest<ProductABC[]>(`/ecommerce/${brandCode}/products`);
  return apiRequest<ProductABC[]>(`/ecommerce/${brandCode}/products`);
}

export async function getEcommerceCustomers(
  brandCode: string,
): Promise<{ cohorts: CohortRow[]; refunds: RefundRow[] }> {
  if (isDemoMode()) return demoRequest(`/ecommerce/${brandCode}/customers`);
  return apiRequest(`/ecommerce/${brandCode}/customers`);
}

export async function getEcommerceTraffic(
  brandCode: string,
): Promise<TrafficRow[]> {
  if (isDemoMode())
    return demoRequest<TrafficRow[]>(`/ecommerce/${brandCode}/traffic`);
  return apiRequest<TrafficRow[]>(`/ecommerce/${brandCode}/traffic`);
}

// ============================================================================
// Amazon Analytics
// ============================================================================

export async function getAmazonOverview(
  brandCode: string,
): Promise<AmazonOverview> {
  if (isDemoMode())
    return demoRequest<AmazonOverview>(`/amazon/${brandCode}/overview`);
  return apiRequest<AmazonOverview>(`/amazon/${brandCode}/overview`);
}

export async function getAmazonMonthly(
  brandCode: string,
): Promise<AmazonMonthlyProfitability[]> {
  if (isDemoMode())
    return demoRequest<AmazonMonthlyProfitability[]>(
      `/amazon/${brandCode}/monthly`,
    );
  return apiRequest<AmazonMonthlyProfitability[]>(
    `/amazon/${brandCode}/monthly`,
  );
}

export async function getAmazonTopSkus(
  brandCode: string,
): Promise<AmazonTopSku[]> {
  if (isDemoMode())
    return demoRequest<AmazonTopSku[]>(`/amazon/${brandCode}/top-skus`);
  return apiRequest<AmazonTopSku[]>(`/amazon/${brandCode}/top-skus`);
}

export async function getAmazonInventory(
  brandCode: string,
): Promise<AmazonInventoryHealth[]> {
  if (isDemoMode())
    return demoRequest<AmazonInventoryHealth[]>(
      `/amazon/${brandCode}/inventory`,
    );
  return apiRequest<AmazonInventoryHealth[]>(`/amazon/${brandCode}/inventory`);
}

export async function getAmazonReturns(
  brandCode: string,
): Promise<AmazonReturnsSku[]> {
  if (isDemoMode())
    return demoRequest<AmazonReturnsSku[]>(`/amazon/${brandCode}/returns`);
  return apiRequest<AmazonReturnsSku[]>(`/amazon/${brandCode}/returns`);
}

// ============================================================================
// Email Schedules
// ============================================================================

export async function getEmailSchedules(): Promise<{
  schedules: EmailSchedule[];
  activeTaskCount: number;
}> {
  if (isDemoMode())
    return demoRequest<{ schedules: EmailSchedule[]; activeTaskCount: number }>(
      "/email-schedules",
    );
  return apiRequest<{ schedules: EmailSchedule[]; activeTaskCount: number }>(
    "/email-schedules",
  );
}

export async function getEmailSchedule(id: string): Promise<EmailSchedule> {
  if (isDemoMode()) return demoRequest<EmailSchedule>(`/email-schedules/${id}`);
  return apiRequest<EmailSchedule>(`/email-schedules/${id}`);
}

export async function createEmailSchedule(
  input: CreateEmailScheduleInput,
): Promise<EmailSchedule> {
  if (isDemoMode()) return demoRequest<EmailSchedule>("/email-schedules");
  return apiRequest<EmailSchedule>("/email-schedules", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateEmailSchedule(
  id: string,
  input: UpdateEmailScheduleInput,
): Promise<EmailSchedule> {
  if (isDemoMode()) return demoRequest<EmailSchedule>(`/email-schedules/${id}`);
  return apiRequest<EmailSchedule>(`/email-schedules/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteEmailSchedule(id: string): Promise<void> {
  if (isDemoMode()) return;
  await apiRequest<{ deleted: boolean }>(`/email-schedules/${id}`, {
    method: "DELETE",
  });
}

export async function toggleEmailSchedule(id: string): Promise<EmailSchedule> {
  if (isDemoMode())
    return demoRequest<EmailSchedule>(`/email-schedules/${id}/toggle`);
  return apiRequest<EmailSchedule>(`/email-schedules/${id}/toggle`, {
    method: "POST",
  });
}

export async function runEmailScheduleNow(
  id: string,
): Promise<{ message: string; status: string; recipients: string[] }> {
  if (isDemoMode())
    return demoRequest<{
      message: string;
      status: string;
      recipients: string[];
    }>(`/email-schedules/${id}/run`);
  return apiRequest<{ message: string; status: string; recipients: string[] }>(
    `/email-schedules/${id}/run`,
    { method: "POST" },
  );
}

export async function sendTestEmail(
  id: string,
  email: string,
): Promise<{ message: string; status: string; recipients: string[] }> {
  if (isDemoMode())
    return {
      message: "Test email sent (demo)",
      status: "success",
      recipients: [email],
    };
  return apiRequest<{ message: string; status: string; recipients: string[] }>(
    `/email-schedules/${id}/test`,
    {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: { "Content-Type": "application/json" },
    },
  );
}

export async function cloneEmailSchedule(id: string): Promise<EmailSchedule> {
  if (isDemoMode())
    return demoRequest<EmailSchedule>(`/email-schedules/${id}/clone`);
  return apiRequest<EmailSchedule>(`/email-schedules/${id}/clone`, {
    method: "POST",
  });
}

export async function getEmailScheduleNextRuns(
  id: string,
): Promise<{ nextRuns: string[] }> {
  if (isDemoMode())
    return demoRequest<{ nextRuns: string[] }>(
      `/email-schedules/${id}/next-runs`,
    );
  return apiRequest<{ nextRuns: string[] }>(`/email-schedules/${id}/next-runs`);
}

export async function getEmailSchedulePortcos(): Promise<
  SchedulePortcoOption[]
> {
  if (isDemoMode())
    return demoRequest<SchedulePortcoOption[]>("/email-schedules/portcos");
  return apiRequest<SchedulePortcoOption[]>("/email-schedules/portcos");
}

// ============================================================================
// Email Execution Log
// ============================================================================

export async function getEmailLogRuns(
  limit = 50,
): Promise<SchedulerRunEntry[]> {
  if (isDemoMode()) return [];
  return apiRequest<SchedulerRunEntry[]>(
    `/reports/email-log/runs?limit=${limit}`,
  );
}

export async function getEmailLogRun(
  runId: string,
): Promise<SchedulerRunEntry> {
  return apiRequest<SchedulerRunEntry>(`/reports/email-log/runs/${runId}`);
}

export async function getEmailLogManualSends(
  limit = 50,
): Promise<EmailLogEntry[]> {
  if (isDemoMode()) return [];
  return apiRequest<EmailLogEntry[]>(
    `/reports/email-log/manual?limit=${limit}`,
  );
}

export async function getEmailHtml(
  emailId: string,
): Promise<EmailHtmlResponse> {
  return apiRequest<EmailHtmlResponse>(
    `/reports/email-log/emails/${emailId}/html`,
  );
}

// ============================================================================
// Email Template Versioning
// ============================================================================

export async function getTemplateMetadata(): Promise<TemplateMetadata> {
  return apiRequest<TemplateMetadata>("/templates/metadata");
}

export async function getTemplateDraft(): Promise<{
  source: string;
  isDraft: boolean;
}> {
  return apiRequest<{ source: string; isDraft: boolean }>("/templates/draft");
}

export async function getTemplateProduction(): Promise<{
  source: string;
  version: number;
}> {
  return apiRequest<{ source: string; version: number }>(
    "/templates/production",
  );
}

export async function saveTemplateDraft(content: string): Promise<void> {
  await apiRequest<{ saved: boolean }>("/templates/draft", {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
}

/**
 * Stage the template shipped with the current build as the draft, so it can be
 * previewed against production and then promoted.
 */
export async function adoptBundledTemplate(): Promise<void> {
  await apiRequest<{ saved: boolean }>("/templates/adopt-bundled", {
    method: "POST",
  });
}

export async function discardTemplateDraft(): Promise<void> {
  await apiRequest<{ discarded: boolean }>("/templates/draft", {
    method: "DELETE",
  });
}

export async function promoteTemplateDraft(
  notes?: string,
): Promise<{ version: number; promotedAt: string }> {
  return apiRequest<{ version: number; promotedAt: string }>(
    "/templates/promote",
    {
      method: "POST",
      body: JSON.stringify({ notes }),
    },
  );
}

export async function getTemplatePreview(
  portcoId: string,
  variant: "draft" | "production",
  calloutMessage?: string,
): Promise<EmailPreviewData> {
  const params = new URLSearchParams({ variant });
  if (calloutMessage) params.set("calloutMessage", calloutMessage);
  return apiRequest<EmailPreviewData>(
    `/templates/preview/${portcoId}?${params.toString()}`,
  );
}

export async function sendTemplateTestEmail(
  portcoId: string,
  variant: "draft" | "production",
  email: string,
): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(
    `/templates/preview/${portcoId}/send-test?variant=${variant}`,
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  );
}
